import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getCreateChargePointDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-charge-point.form-default-values';
import { CreateChargePointModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-charge-point.model';
import { CreatePhysicalSocketBaseModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-physical-socket.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoCreateChargePointMutation } from '@service/cpms-bo-charge-point.service';

import { CreateChargePointContextProvider } from '../charge-point.context';
import { ChargePointCreateFormController } from './charge-point-create-form/charge-point-create-form.controller';

interface ChargePointCreateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const ChargePointCreateController = ({
  onCancel,
  onSuccess,
}: ChargePointCreateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [createChargePointMutation] = useCpmsBoCreateChargePointMutation();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<CreateChargePointModel> = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.charge-point-create.error', { error: t('global.unknown') }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<CreateChargePointModel> = (
    fieldValues,
  ) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    let ocppConnectorId = 1;

    const createChargePoint = {
      ...fieldValues,
      channels: fieldValues.channels.map((channel) => {
        if (!channel.physicalSockets) {
          return channel;
        }
        return {
          ...channel,
          physicalSockets: channel.physicalSockets.map((physicalSocket) => {
            const ps: CreatePhysicalSocketBaseModel = {
              ...physicalSocket,
              ocppConnectorId,
            };
            ocppConnectorId += 1;
            return ps;
          }),
        };
      }),
    };

    createChargePointMutation(createChargePoint)
      .unwrap()
      .then(() => {
        toast.success(t('$.charge-point-create.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(t('$.charge-point-create.error', { error: errorMessage }));
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getCreateChargePointDefaultValues();

  return (
    <CreateChargePointContextProvider data={data}>
      <ChargePointCreateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </CreateChargePointContextProvider>
  );
};
